import type { AppAction, AppState, ThunkAction, APIError } from '../../types/app.types';
import type {
  V2Response,
  V2PharmacySearchResult,
  PriceCategory,
  TierCategory,
  V2Pharmacy,
} from '../../types/api-v2.types';
import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import { decamelizeKeys } from 'humps';
import { stringify } from 'query-string';
import { getUserMagicTier } from './available-pricing-tiers-module';
import type { AccountState } from './account-module';
import { isMagicPriceCategory, updateCartPharmacy, hasMagicMeds } from './cart-module';
import type { Item } from './cart-module';

export type UpdateSelectedPharmacy = {
  type: 'pharmacy/UPDATE_SELECTED_PHARMACY';
  selectedPharmacy: V2Pharmacy | null | undefined;
};
export type UpdateSmartPharmacy = {
  type: 'pharmacy/UPDATE_SMART_PHARMACY';
  smartPharmacy: V2Pharmacy | null | undefined;
};
export type UpdateMagicPharmacy = {
  type: 'pharmacy/UPDATE_MAGIC_PHARMACY';
  magicPharmacy: V2Pharmacy | null | undefined;
};
export type SetPharmacySearchZip = {
  type: 'pharmacy/SET_PHARMACY_SEARCH_ZIP';
  zipCode: string | null | undefined;
};
export type SetEditPharmacyMode = {
  type: 'pharmacy/SET_EDIT_PHARMACY_MODE';
  editPharmacyMode: string | null | undefined;
};
export type PharmacyAction =
  | UpdateSelectedPharmacy
  | UpdateSmartPharmacy
  | UpdateMagicPharmacy
  | SetPharmacySearchZip
  | SetEditPharmacyMode;
export type PharmacyState = {
  accountPharmacy: V2Pharmacy | null | undefined;
  smartPharmacy: V2Pharmacy | null | undefined;
  magicPharmacy: V2Pharmacy | null | undefined;
  selectedPharmacy: V2Pharmacy | null | undefined;
  searchZipCode: string | null | undefined;
  editPharmacyMode: string | null | undefined;
};
const initialState: PharmacyState = {
  accountPharmacy: null,
  smartPharmacy: null,
  magicPharmacy: null,
  selectedPharmacy: null,
  searchZipCode: null,
  editPharmacyMode: null,
};
export const isSuperSaver = (state: PharmacyState) =>
  Boolean(state.selectedPharmacy && state.selectedPharmacy.isSupersaver);

export function getSelectedPharmacyNetwork(state: PharmacyState) {
  return state.selectedPharmacy && state.selectedPharmacy.networkId;
}

export function getPharmacyName(pharmacy: V2Pharmacy | null | undefined): string {
  return (pharmacy && pharmacy.brand && pharmacy.brand.formattedName) || (pharmacy && pharmacy.name) || '';
}

export function getUserPharmacyLocationId(pharmacy: V2Pharmacy | null | undefined): string | null | undefined {
  return pharmacy && pharmacy.location && pharmacy.location.id;
}

export function findTierCategoryForPriceCategory(priceCategory: PriceCategory): TierCategory {
  if (!priceCategory) {
    return 'edlp';
  }

  if (isMagicPriceCategory(priceCategory)) {
    return 'magic';
  }

  switch (priceCategory) {
    case 'local':
      return 'local';

    case 'edlp':
      return 'edlp';

    case 'delivery':
      return 'delivery';

    default:
      return 'edlp';
  }
}

export function getCartTier(
  pharmacyState: PharmacyState,
  accountState: AccountState,
  cartItemsState: Array<Item>
): TierCategory {
  if (cartItemsState.length === 0) {
    return getPharmacyTier(pharmacyState.selectedPharmacy);
  }

  const magicMed = cartItemsState.find(
    (item) => item && item.priceCategory && isMagicPriceCategory(item.priceCategory)
  );
  const medPriceCategory = !magicMed ? cartItemsState[0].priceCategory : magicMed.priceCategory;
  return findTierCategoryForPriceCategory(medPriceCategory);
}

export function getPharmacyTier(pharmacy: V2Pharmacy | null | undefined): TierCategory {
  if (pharmacy && pharmacy.isDelivery) return 'delivery';
  if (pharmacy && pharmacy.isSupersaver) return 'local';
  return 'edlp';
}

export function pharmacyTierToNetworkId(priceCategory: PriceCategory | null | undefined, state: AppState): number {
  if (priceCategory === 'magic') {
    const magicTier = getUserMagicTier(state);

    if (magicTier) {
      return magicTier.pharmacyNetworkId;
    }
  }

  if (priceCategory === 'delivery') return 3;
  if (priceCategory === 'local') return 2;
  return 1;
}

export function getNearbySmartPharmacy(zipCode?: string | null | undefined): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const queryString = zipCode
      ? `/v2/pharmacies?zip_code=${zipCode}&only_supersaver=true&limit=1&radius=9999`
      : `/v2/pharmacies?only_supersaver=true&limit=1&radius=9999`;
    return blinkAPIClient
      .get(queryString)
      .then((res) => res.data)
      .then((data: V2Response<V2PharmacySearchResult>) => data.result)
      .then((data) => data.results)
      .then((data: Array<V2Pharmacy>) => dispatch(updateSmartPharmacy(data[0])))
      .catch(() => {
        // if this fails we'll end up just showing the old pharmacy
      });
  };
}

export function getNearbyMagicPharmacy(
  pharmacyNetworkId: number,
  zipCode: string | null | undefined
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    let queryString = `/v2/pharmacies?network_ids=${pharmacyNetworkId}`;
    queryString += zipCode ? `&zip_code=${zipCode}` : '&limit=1&radius=9999';
    return blinkAPIClient
      .get(queryString)
      .then((res) => res.data)
      .then((data: V2Response<V2PharmacySearchResult>) => data.result)
      .then((data) => data.results)
      .then((data: Array<V2Pharmacy>) => dispatch(updateMagicPharmacy(data[0])))
      .catch(() => {
        // if this fails we'll end up just showing the old pharmacy
      });
  };
}

export function setPharmacySearchZip(zipCode: string | null | undefined): SetPharmacySearchZip {
  return {
    type: 'pharmacy/SET_PHARMACY_SEARCH_ZIP',
    zipCode,
  };
}

export function setEditPharmacyMode(editPharmacyMode: string | null | undefined): SetEditPharmacyMode {
  return {
    type: 'pharmacy/SET_EDIT_PHARMACY_MODE',
    editPharmacyMode,
  };
}

type PharmacySearchOptions = {
  zipCode?: number;
  lat?: number;
  lng?: number;
  name?: string;
  limit?: number;
  radius?: number;
  brands?: string[];
  excludeBrands?: string[];
  onlySmartDeal?: boolean;
  allowOutOfNetwork?: boolean;
  allowUnaffiliated?: boolean;
  search?: string;
};
let pharmacySearchCanceller: CancelTokenSource;

export function pharmacySearch(options: PharmacySearchOptions): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const opts = { ...options };

    if (pharmacySearchCanceller) {
      pharmacySearchCanceller.cancel(); // only handle one in-flight request at a time
    }

    pharmacySearchCanceller = axios.CancelToken.source();
    return blinkAPIClient
      .get(`/v2/pharmacies?${stringify(decamelizeKeys(opts))}`, {
        cancelToken: pharmacySearchCanceller.token,
      })
      .then((res) => res.data)
      .then((data: V2Response<V2PharmacySearchResult>) => data.result)
      .catch((error: APIError) => {
        return error;
      });
  };
}

export function fetchPreferredPharmacyInfo(pharmacyId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`v2/pharmacies/preferred/${pharmacyId}`)
      .then((res) => res.data)
      .then((data) => data.result);
  };
}

export function changeCartPharmacy(pharmacy: V2Pharmacy): ThunkAction<Promise<any>> {
  return function (dispatch, getState): Promise<any> {
    const state = getState();
    const magicTier = getUserMagicTier(state);
    const cartHasMagicMeds = hasMagicMeds(state);
    const networkId =
      cartHasMagicMeds && magicTier ? magicTier.pharmacyNetworkId : parseInt(pharmacy.networkId as string, 10);
    return dispatch(
      updateCartPharmacy({
        pharmacy,
        force: true,
        pharmacyNetworkId: networkId,
      })
    );
  };
}

export function updateSelectedPharmacy(selectedPharmacy: V2Pharmacy | null | undefined): UpdateSelectedPharmacy {
  return {
    type: 'pharmacy/UPDATE_SELECTED_PHARMACY',
    selectedPharmacy: selectedPharmacy,
  };
}

export function updateSmartPharmacy(smartPharmacy: V2Pharmacy): UpdateSmartPharmacy {
  return {
    type: 'pharmacy/UPDATE_SMART_PHARMACY',
    smartPharmacy: smartPharmacy,
  };
}

export function updateMagicPharmacy(magicPharmacy: V2Pharmacy): UpdateMagicPharmacy {
  return {
    type: 'pharmacy/UPDATE_MAGIC_PHARMACY',
    magicPharmacy: magicPharmacy,
  };
}

// Selectors
export function getPharmacyId(pharmacy: V2Pharmacy | null | undefined): string | null | undefined {
  return pharmacy && pharmacy.location && pharmacy.location.id;
}

export default function pharmacyReducer(state: PharmacyState = { ...initialState }, action: AppAction): PharmacyState {
  switch (action.type) {
    case 'account/GET_SUMMARY_SUCCESS': {
      const { selectedPharmacy } = state; // preserve selectedPharmacy from previous state

      const { pharmacy, magicPharmacy } = action.data;
      return {
        ...state,
        accountPharmacy: pharmacy,
        magicPharmacy,
        selectedPharmacy: selectedPharmacy || magicPharmacy,
      };
    }

    case 'pharmacy/UPDATE_SELECTED_PHARMACY':
      return { ...state, selectedPharmacy: action.selectedPharmacy };

    case 'pharmacy/UPDATE_SMART_PHARMACY':
      return { ...state, smartPharmacy: action.smartPharmacy };

    case 'pharmacy/UPDATE_MAGIC_PHARMACY':
      return { ...state, magicPharmacy: action.magicPharmacy };

    case 'cart/REQ_CART_SUCCESS':
      return { ...state };

    case 'pharmacy/SET_PHARMACY_SEARCH_ZIP':
      return { ...state, searchZipCode: action.zipCode };

    case 'pharmacy/SET_EDIT_PHARMACY_MODE':
      return { ...state, editPharmacyMode: action.editPharmacyMode };

    default:
      return state;
  }
}
